import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Play,
  Pause,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { formatRelativeTime } from '../utils/format';
import type { SocketCloudServiceOrchestrationComponents } from '@aaimplatform/socket-cloud-types';

type ScheduledJob = SocketCloudServiceOrchestrationComponents['schemas']['ScheduledJob'];
type JobRun = SocketCloudServiceOrchestrationComponents['schemas']['JobRun'];
type JobRunStatus = SocketCloudServiceOrchestrationComponents['schemas']['JobRunStatus'];

// CSRF double-submit echo (cookie-session-auth canon primitive 5): the sc
// realm sets the non-HttpOnly `sc-csrf-token` nonce cookie at login; every
// cookie-authenticated mutation must echo it as X-CSRF-Token or the API 403s.
function readCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)sc-csrf-token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

async function orchFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (method !== 'GET') {
    headers['X-CSRF-Token'] = readCsrfToken();
    if (init?.body) headers['content-type'] = 'application/json';
  }
  // Same-origin: the base is '' and the literal carries the whole spec-key
  // prefix, /api/v1/orchestration (api-version-canonical.md).
  const response = await fetch(`/api/v1/orchestration${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      detail = body.message || body.error || detail;
    } catch {
      // non-JSON error body; keep the status line
    }
    throw new Error(detail);
  }
  return (await response.json()) as T;
}

const RUN_STATUS_BADGE: Record<JobRunStatus, 'success' | 'warning' | 'danger' | 'primary' | 'secondary'> = {
  PENDING: 'secondary',
  SUBMITTED: 'primary',
  RUNNING: 'primary',
  SUCCEEDED: 'success',
  FAILED: 'danger',
  SUBMIT_FAILED: 'danger',
  TIMED_OUT: 'danger',
  MISSED: 'warning',
};

const ORCH_KEYS = {
  jobs: () => ['orchestration', 'jobs'] as const,
  runs: (jobId: string) => ['orchestration', 'jobs', jobId, 'runs'] as const,
};

function JobRunsList({ jobId }: { jobId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ORCH_KEYS.runs(jobId),
    queryFn: () => orchFetch<{ data: JobRun[]; count: number }>(`/jobs/${jobId}/runs`),
    refetchInterval: 15000,
  });

  if (isLoading) return <div className="text-sm text-gray-400 py-2">Loading runs...</div>;
  if (error) {
    return (
      <div className="text-sm text-danger-400 py-2 flex items-center gap-2">
        <AlertCircle className="w-4 h-4" />
        {error instanceof Error ? error.message : 'Failed to load runs'}
      </div>
    );
  }
  const runs = data?.data ?? [];
  if (runs.length === 0) return <div className="text-sm text-gray-400 py-2">No runs yet.</div>;

  return (
    <div className="space-y-1">
      {runs.map((run) => (
        <div key={run.id} className="flex items-center gap-3 py-1.5 border-b border-gray-800 last:border-0 text-sm">
          <Badge variant={RUN_STATUS_BADGE[run.status] ?? 'default'} size="sm">
            {run.status}
          </Badge>
          <span className="text-gray-300">{run.triggerType}</span>
          <span className="text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatRelativeTime(new Date(run.scheduledFor))}
          </span>
          {run.batchJobId && (
            <span className="text-gray-500 font-mono text-xs truncate max-w-48" title={run.batchJobId}>
              {run.batchJobId}
            </span>
          )}
          {run.errorCode && (
            <span className="text-danger-400 text-xs" title={run.error ?? undefined}>
              {run.errorCode}
            </span>
          )}
          {typeof run.exitCode === 'number' && <span className="text-gray-500 text-xs">exit {run.exitCode}</span>}
        </div>
      ))}
    </div>
  );
}

function JobCard({ job }: { job: ScheduledJob }) {
  const [expanded, setExpanded] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ORCH_KEYS.jobs() });
    void queryClient.invalidateQueries({ queryKey: ORCH_KEYS.runs(job.id) });
  };

  const triggerNow = useMutation({
    mutationFn: () => orchFetch<JobRun>(`/jobs/${job.id}/trigger`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      setExpanded(true);
      invalidate();
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Trigger failed'),
  });

  const pauseResume = useMutation({
    mutationFn: () =>
      orchFetch<ScheduledJob>(`/jobs/${job.id}/${job.paused ? 'resume' : 'pause'}`, { method: 'POST' }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : 'Update failed'),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            className="flex items-center gap-2 text-left min-w-0"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium text-gray-100 truncate">{job.name}</div>
              {job.description && <div className="text-xs text-gray-400 truncate">{job.description}</div>}
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            {!job.enabled && (
              <Badge variant="danger" size="sm">
                disabled
              </Badge>
            )}
            {job.enabled && job.paused && (
              <Badge variant="warning" size="sm">
                paused
              </Badge>
            )}
            {job.enabled && !job.paused && (
              <Badge variant="success" size="sm">
                active
              </Badge>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={pauseResume.isPending || !job.enabled}
              onClick={() => pauseResume.mutate()}
              title={job.paused ? 'Resume schedule' : 'Pause schedule (manual trigger stays available)'}
            >
              {job.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              {job.paused ? 'Resume' : 'Pause'}
            </Button>
            <Button
              size="sm"
              disabled={triggerNow.isPending || !job.enabled}
              onClick={() => triggerNow.mutate()}
              title="Submit one run now (does not move the schedule)"
            >
              <Play className="w-4 h-4" />
              Trigger now
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-400">
          <span className="font-mono">{job.cronExpression} UTC</span>
          <span>
            next fire:{' '}
            {job.nextFireAt ? (
              <span className="text-gray-300">{formatRelativeTime(new Date(job.nextFireAt))}</span>
            ) : (
              <span className="text-gray-500">--</span>
            )}
          </span>
          <span>
            target: <span className="text-gray-300">{job.targetConfig.jobDefinition}</span> on{' '}
            <span className="text-gray-300">{job.targetConfig.jobQueue}</span> ({job.targetConfig.region})
          </span>
        </div>
        {actionError && (
          <div className="mt-2 text-sm text-danger-400 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {actionError}
          </div>
        )}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-800">
            <JobRunsList jobId={job.id} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function OrchestrationPage() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ORCH_KEYS.jobs(),
    queryFn: () => orchFetch<{ data: ScheduledJob[]; count: number }>('/jobs'),
    refetchInterval: 30000,
  });

  const jobs = data?.data ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarClock className="w-6 h-6 text-primary-400" />
          <div>
            <h1 className="text-xl font-semibold text-gray-100">Orchestration</h1>
            <p className="text-sm text-gray-400">Scheduled jobs dispatched to the AWS Batch compute substrate</p>
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading && <div className="text-gray-400 text-sm">Loading scheduled jobs...</div>}
      {error !== null && error !== undefined && (
        <div className="text-danger-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error instanceof Error ? error.message : 'Failed to load scheduled jobs'}
        </div>
      )}
      {!isLoading && !error && jobs.length === 0 && (
        <Card>
          <CardContent>
            <div className="text-gray-400 text-sm py-4">
              No scheduled jobs registered. Jobs are registered via POST /api/v1/orchestration/jobs.
            </div>
          </CardContent>
        </Card>
      )}
      <div className="space-y-3">
        {jobs.map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>
    </div>
  );
}
